// Matching schedule activities to cost codes.
//
// Schedules from the field carry no JDE phase — on Block F, zero of 117
// activities had one. So the only bridge between the schedule (which has dates)
// and the budget (which has quantities) is the activity NAME, and somebody has
// to confirm it. This suggests the pairing; the setup step lets a human fix it.
//
// The suggestion is stored, so the next schedule upload re-drives dates through
// the same bindings instead of being re-keyed.

// Words that carry no signal in a pipeline activity name. 'ROW' is deliberately
// NOT here — it distinguishes 'Clear ROW' from 'Clear'.
const STOP = new Set(['and', 'the', 'of', 'to', 'for', 'a', 'an', 'at', 'in', 'on', 'with', 'crew']);

// Trim the endings that make the same scope read differently in two systems:
// 'Hydrotesting' vs 'Hydrotest', 'Bores' vs 'Bore', 'Bending' vs 'Bend'.
//
// Each rule only fires if it leaves a real word behind (4+ characters). Without
// that guard the stemmer eats genuine words and stops matching things it should:
// 'string' reduced to 'str' while 'stringing' reduced to 'string', so
// 'Stringing' never matched 'String Pipe'.
const MIN_STEM = 4;
const trim = (w, re) => {
    const out = w.replace(re, '');
    return out.length >= MIN_STEM ? out : w;
};
// Plurals strip only the trailing 's': taking 'es' off 'bores' leaves 'bor',
// which fails the guard and leaves the word unstemmed, so 'Bores' stopped
// matching 'Bore' entirely.
const stem = (w) => trim(trim(trim(w, /(ings|ing)$/), /s$/), /e$/);

export const tokenize = (name) => String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((w) => w && !STOP.has(w) && !/^\d+$/.test(w))
    .map(stem)
    .filter(Boolean);

/**
 * 0–1 similarity between two names.
 *
 * Containment is weighted over Jaccard on purpose: 'Tie-ins' should match
 * 'Tie-in Crew#1' strongly even though the activity name carries extra words.
 */
export const nameScore = (a, b) => {
    const A = new Set(tokenize(a));
    const B = new Set(tokenize(b));
    if (A.size === 0 || B.size === 0) return 0;
    let shared = 0;
    A.forEach((t) => { if (B.has(t)) shared += 1; });
    if (shared === 0) return 0;
    const containment = shared / Math.min(A.size, B.size);
    const jaccard = shared / (A.size + B.size - shared);
    return 0.7 * containment + 0.3 * jaccard;
};

// Below this a suggestion is noise, and a wrong auto-binding is worse than none.
const THRESHOLD = 0.45;
// Activities within this much of the best score are taken too, so a cost code
// split across four tie-in crews binds to all four rather than an arbitrary one.
const TIE_BAND = 0.12;

const nameOf = (a) => String(a.Activity_Name || a.activityName || '').trim();
const pathOf = (a) => String(a.WBS_Path || a.wbsPath || '');

/** Names that appear as a path segment on some other activity are summaries. */
const summaryNames = (activities) => {
    const parents = new Set();
    activities.forEach((a) => {
        pathOf(a).split('>').forEach((part) => {
            const p = part.trim();
            if (p) parents.add(p.toLowerCase());
        });
    });
    return parents;
};

/**
 * Summary rows repeat as a path segment on their children, and binding to one
 * hands back the whole parent span instead of the real work dates.
 */
export const leafActivities = (activities = []) => {
    const parents = summaryNames(activities);
    return activities.filter((a) => !parents.has(nameOf(a).toLowerCase()));
};

/** The leaf activities underneath a summary. */
const descendantsOf = (summary, activities) => {
    const key = nameOf(summary).toLowerCase();
    if (!key) return [];
    const parents = summaryNames(activities);
    return activities.filter((a) => (
        !parents.has(nameOf(a).toLowerCase())
        && pathOf(a).split('>').some((part) => part.trim().toLowerCase() === key)
    ));
};

/**
 * Best activity matches for one cost code.
 * Returns [{ activity, score }] — possibly several, best first.
 *
 * Summaries are scored too, then expanded to their children. That is what
 * catches the crossings: the schedule lists them individually by location
 * ('County Road 415 Sta. 543+34'), which no name match can tie to cost code
 * 141803, but their parent is plainly 'Bores'.
 */
export const activityKey = (a) => `${a.Activity_Code || a.activityCode || ''}|${nameOf(a)}`;

// A summary whose children mostly belong to OTHER cost codes is not a work
// package, it is a container the scheduler nested things under. Block F parents
// lower-in, backfill, four tie-in crews and hydrotest beneath 'HDD'; expanding
// that would hand cost code 141903 two months of somebody else's work.
const MIXED_LIMIT = 0.5;

/**
 * @param owner  optional Map(activityKey → costCode) from a global first pass.
 *               Without it summaries expand to every child, which is right for
 *               a single-row re-suggest but wrong when seeding a whole schedule.
 */
export const suggestActivities = (row, activities = [], owner = null) => {
    const label = row?.description || row?.Description || '';
    const code = row?.costCode || row?.Cost_Code || null;
    const parents = summaryNames(activities);

    // A schedule that carries the JDE phase has already answered the question —
    // name matching exists because most don't. Tagged activities win outright
    // and never get diluted by a fuzzy tie-band. matchedBy is what separates
    // them in the UI: a perfect name match also scores 1 (a stemmed
    // 'Hydrotesting' IS 'Hydrotest'), so the score alone can't say whether a
    // human should check the binding.
    if (code) {
        const tagged = activities.filter((a) => String(a.Cost_Code ?? a.costCode ?? '') === String(code));
        // Summaries carrying the code would drag in every child; the leaves
        // under them are the work, and they carry the same tag.
        const leaves = tagged.filter((a) => !parents.has(nameOf(a).toLowerCase()));
        const picked = leaves.length > 0 ? leaves : tagged;
        if (picked.length > 0) return picked.map((activity) => ({ activity, score: 1, matchedBy: 'code' }));
    }

    if (!label) return [];

    const scored = activities
        .map((activity) => ({ activity, score: nameScore(label, nameOf(activity)) }))
        .filter((s) => s.score >= THRESHOLD)
        .sort((a, b) => b.score - a.score);
    if (scored.length === 0) return [];

    const best = scored.filter((s) => s.score >= scored[0].score - TIE_BAND);

    const out = [];
    const seen = new Set();
    best.forEach(({ activity, score }) => {
        let targets = [activity];
        if (parents.has(nameOf(activity).toLowerCase())) {
            const kids = descendantsOf(activity, activities);
            if (kids.length === 0) {
                targets = [activity];                       // a summary with no children
            } else if (!owner) {
                targets = kids;
            } else {
                const mine = kids.filter((k) => owner.get(activityKey(k)) === code);
                const others = kids.filter((k) => {
                    const o = owner.get(activityKey(k));
                    return o && o !== code;
                });
                // Homogeneous: take the whole package, including the unclaimed
                // children — that is how 'Bores' picks up crossings named only
                // by location. Mixed: keep just what this code already owns.
                targets = others.length / kids.length < MIXED_LIMIT
                    ? kids.filter((k) => !others.includes(k))
                    : mine;
            }
        }
        targets.forEach((t) => {
            const key = activityKey(t);
            if (seen.has(key)) return;
            seen.add(key);
            out.push({ activity: t, score, matchedBy: 'name' });
        });
    });
    return out;
};

/**
 * Which cost code each activity most plausibly belongs to, across the whole
 * schedule. Needed before expanding summaries so a child that clearly belongs
 * to another code isn't dragged along by its parent.
 */
export const ownershipMap = (rows = [], activities = []) => {
    const owner = new Map();
    activities.forEach((a) => {
        let bestCode = null;
        let bestScore = THRESHOLD;
        rows.forEach((r) => {
            const s = nameScore(r.Description ?? r.description ?? '', nameOf(a));
            if (s > bestScore) { bestScore = s; bestCode = String(r.Cost_Code ?? r.costCode ?? ''); }
        });
        if (bestCode) owner.set(activityKey(a), bestCode);
    });
    return owner;
};

const minDate = (dates) => dates.filter(Boolean).sort()[0] || null;
const maxDate = (dates) => dates.filter(Boolean).sort().slice(-1)[0] || null;

/**
 * Dates a set of bound activities implies: earliest start to latest finish, so
 * four parallel tie-in crews collapse to the one span the cost code is worked
 * over. Actual dates win over planned ones where the schedule has them.
 */
export const datesFromActivities = (activities = []) => {
    if (activities.length === 0) return { startDate: null, finishDate: null };
    return {
        startDate: minDate(activities.map((a) => a.Actual_Start || a.actualStart || a.Start_Date || a.startDate)),
        finishDate: maxDate(activities.map((a) => a.Finish_Date || a.finishDate || a.Actual_Finish || a.actualFinish)),
    };
};

const codeOf = (a) => a.Activity_Code || a.activityCode || '';

const num = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

/**
 * The install quantity a set of bound activities carries, if any.
 *
 * A scheduler who loaded quantities onto the schedule knows something the
 * budget doesn't — the budget's number is what was bid, the schedule's is what
 * is planned to go in the ground — so it wins when it exists. It usually
 * doesn't: most exports carry only labor units, which the parsers deliberately
 * keep out of Budget_Qty.
 */
export const scheduleQuantity = (activities = []) => {
    let qty = 0;
    let uom = null;
    activities.forEach((a) => {
        const q = num(a.Budget_Qty ?? a.budgetQty);
        if (!q) return;
        qty += q;
        if (!uom) uom = a.UOM || a.uom || null;
    });
    return { qty, uom };
};

/**
 * Seed a quantity schedule from the baseline rows and the schedule activities.
 * The schedule supplies the dates, and the quantity when it has one; otherwise
 * the baseline's budgeted quantity/UOM/hours stand.
 */
export const seedQuantitySchedule = (baselineRows = [], activities = []) => {
    // Cost-code rows only — the time-phased rows share the table.
    const codes = baselineRows.filter((r) => !(r.Period_Date || r.periodDate));
    const owner = ownershipMap(codes, activities);

    return codes.map((r, i) => {
        const description = r.Description ?? r.description ?? '';
        const costCode = String(r.Cost_Code ?? r.costCode ?? '');
        const suggestions = suggestActivities({ description, costCode }, activities, owner);
        const bound = suggestions.map((s) => s.activity);
        const { startDate, finishDate } = datesFromActivities(bound);
        const budgetQty = Number(r.Budget_Qty ?? r.budgetQty) || 0;
        const scheduled = scheduleQuantity(bound);
        const fromSchedule = scheduled.qty > 0;
        return {
            costCode,
            description,
            uom: (fromSchedule ? scheduled.uom : null) || r.UOM || r.uom || '',
            qty: fromSchedule ? scheduled.qty : budgetQty,
            // Both are kept so the mapping panel can show them side by side and
            // let a PM switch back when the schedule's units are wrong.
            qtySource: fromSchedule ? 'schedule' : 'budget',
            budgetQty,
            scheduleQty: scheduled.qty || null,
            scheduleUom: scheduled.uom || null,
            budgetHours: Number(r.Budget_Hours ?? r.budgetHours) || null,
            // The tracker's own start date is the better source when it has one;
            // it reflects when the crew actually broke ground.
            startDate: r.Start_Date || r.startDate || startDate || null,
            finishDate: finishDate || r.Finish_Date || r.finishDate || null,
            dailyRate: null,
            curve: 'linear',
            workDays: 6,
            activityCode: bound.map(codeOf).filter(Boolean).join(', ') || null,
            // The same activity name recurs under each pipeline scope, so the
            // display collapses duplicates while the dates still span them all.
            matchedNames: [...new Set(bound.map((a) => a.Activity_Name || a.activityName).filter(Boolean))],
            matchCount: bound.length,
            matchScore: suggestions[0]?.score ?? 0,
            matchedBy: suggestions[0]?.matchedBy ?? null,
            sortOrder: i + 1,
        };
    });
};
